import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { CdkObserveContent } from '@angular/cdk/observers';

import { debounceTime, Subject } from 'rxjs';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { MonitoringLogger } from '@fcom/core';

import { CarouselTheme } from './enums';
import { IconButtonTheme, IconButtonSize } from '../buttons/icon-button/enums';
import { IconButtonComponent } from '../buttons/icon-button/icon-button.component';

const SCROLL_RATIO_DEFAULT = 0.9;
export type carouselAlign = 'left' | 'center' | 'right';

@Component({
  selector: 'fcom-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconButtonComponent, NgClass, CdkObserveContent],
})
export class CarouselComponent implements AfterViewInit {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private logger = inject(MonitoringLogger, { optional: true });

  public readonly CarouselTheme = CarouselTheme;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconLibrary = IconLibrary;
  readonly IconButtonSize = IconButtonSize;

  @ViewChild('content', { static: true })
  content: ElementRef;

  @ViewChild('contentWrap', { static: true })
  contentWrap: ElementRef;

  leftIcon = input<SvgLibraryIcon>(SvgLibraryIcon.CHEVRON_LEFT);
  rightIcon = input<SvgLibraryIcon>(SvgLibraryIcon.CHEVRON_RIGHT);
  align = input<carouselAlign>('left');
  navigateFullWidth = input(false);
  prevButtonAriaLabel = input.required<string>();
  nextButtonAriaLabel = input.required<string>();
  hasGreyBackground = input(false);
  theme = input<CarouselTheme>(CarouselTheme.DEFAULT);
  initialElementToFocus = input<string>();
  hasContainerShadow = input(true);
  iconButtonSize = input<IconButtonSize>(IconButtonSize.MEDIUM);

  readonly prevButtonVisible = signal(false);
  readonly nextButtonVisible = signal(false);
  readonly offset = signal(0);

  readonly scrollRatio = computed<number>(() => (this.navigateFullWidth() ? 1 : SCROLL_RATIO_DEFAULT));

  private readonly scrollSettle$ = new Subject<void>();
  private readonly SCROLL_SETTLE_DEBOUNCE_MS = 100;

  constructor() {
    this.scrollSettle$.pipe(debounceTime(this.SCROLL_SETTLE_DEBOUNCE_MS), takeUntilDestroyed()).subscribe(() => {
      this.updateButtonVisibility();
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateButtonVisibility();
  }

  @HostListener('scroll')
  onScroll(): void {
    this.offset.set(this.contentWrap.nativeElement.scrollLeft);
    this.scrollSettle$.next();
  }

  ngAfterViewInit(): void {
    if (this.content.nativeElement.children?.length > 0) {
      setTimeout(() => {
        this.updateButtonVisibility();
      }, 0);
    } else {
      this.logger?.error('Tried to initialize carousel without content');
    }

    if (this.initialElementToFocus()) {
      setTimeout(() => {
        this.scrollToElement(this.initialElementToFocus());
      }, 0);
    }
  }

  onContentChange(): void {
    setTimeout(() => {
      this.updateButtonVisibility();
      this.changeDetectorRef.detectChanges();
    }, 0);
  }

  scrollPrev(): void {
    const containerWidth = this.contentWrap.nativeElement.offsetWidth;
    const scrollRatio = this.scrollRatio();
    const offset = this.offset();
    const newOffset = offset > containerWidth ? offset - containerWidth * scrollRatio : 0;
    this.contentWrap.nativeElement.scrollLeft = newOffset;
    this.offset.set(newOffset);
    this.updateButtonVisibility();
  }

  scrollNext(): void {
    const containerWidth = this.contentWrap.nativeElement.offsetWidth as number;
    const contentWidth = this.content.nativeElement.children[0].scrollWidth as number;
    const scrollRatio = this.scrollRatio();
    const offset = this.offset();
    const newOffset =
      contentWidth > containerWidth + offset + containerWidth * scrollRatio
        ? offset + containerWidth * scrollRatio
        : contentWidth - containerWidth;
    this.contentWrap.nativeElement.scrollLeft = newOffset;
    this.offset.set(newOffset);
    this.updateButtonVisibility();
  }

  scrollToElement(elementId: string): void {
    const elementToScroll = this.content.nativeElement.querySelector(`#${this.escapeSelector(elementId)}`);
    if (!elementToScroll) return;

    const container = this.contentWrap.nativeElement;
    const elementLeft = elementToScroll.offsetLeft;
    const elementWidth = elementToScroll.offsetWidth;
    const containerWidth = container.offsetWidth;
    const newScrollLeft = elementLeft - containerWidth / 2 + elementWidth / 2;

    container.scrollLeft = newScrollLeft;
    this.offset.set(newScrollLeft);
    this.updateButtonVisibility();
  }

  scrollTo(value: number): void {
    this.contentWrap.nativeElement.scrollLeft = value;
  }

  private updateButtonVisibility(): void {
    const containerWidth = this.contentWrap.nativeElement.offsetWidth as number;
    const contentWidth = this.content.nativeElement.children[0].scrollWidth as number;
    const offset = this.offset();

    if (contentWidth > containerWidth) {
      if (containerWidth + offset >= contentWidth) {
        this.prevButtonVisible.set(true);
        this.nextButtonVisible.set(false);
      } else if (offset > 0) {
        this.prevButtonVisible.set(true);
        this.nextButtonVisible.set(true);
      } else {
        this.prevButtonVisible.set(false);
        this.nextButtonVisible.set(true);
      }
    } else {
      this.prevButtonVisible.set(false);
      this.nextButtonVisible.set(false);
    }
  }

  private escapeSelector(value: string): string {
    if (!value) {
      return value;
    }
    return value.replace(/[!"#$%&'()*+,-./:;<=>?@[\\\]^`{|}~\s]/g, (char) => `\\${char}`);
  }
}
